import React from 'react';
import { cn } from '@/lib/utils';

interface SvgFlagProps {
  countryCode: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  fallback?: string;
  onError?: () => void;
}

const SIZE_CLASSES = {
  sm: 'h-4 w-6',    // 16px height, ~24px width (3:2 ratio)
  md: 'h-6 w-9',    // 24px height, ~36px width
  lg: 'h-8 w-12',   // 32px height, ~48px width  
  xl: 'h-12 w-18'   // 48px height, ~72px width
};

export const SvgFlag: React.FC<SvgFlagProps> = ({ 
  countryCode, 
  size = 'md', 
  className = '',
  fallback,
  onError
}) => {
  const [hasError, setHasError] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  
  const normalizedCode = countryCode.toLowerCase();
  const flagUrl = `/icons/flag_svg/${normalizedCode}.svg`;
  
  // Reset error state when countryCode changes
  React.useEffect(() => {
    setHasError(false);
    setIsLoading(true);
  }, [countryCode]);
  
  const handleError = () => {
    console.log('Flag not found:', flagUrl);
    // Try fallback flag (no.svg) if not already trying it
    if (normalizedCode !== 'no') {
      console.log('Trying fallback flag (no.svg) for:', countryCode);
      // Don't set error yet, let the fallback image load
    } else {
      setHasError(true);
    }
    setIsLoading(false);
    onError?.();
  };
  
  const handleLoad = () => {
    setIsLoading(false);
  };
  
  // If no country code provided, show fallback
  if (!countryCode || countryCode.trim() === '') {
    return fallback ? (
      <span className={cn('text-muted-foreground', className)}>
        {fallback}
      </span>
    ) : null;
  }
  
  
  // Show fallback if error occurred
  if (hasError) {
    return fallback ? (
      <div 
        className={cn(
          'flex items-center justify-center border border-border rounded-sm bg-muted text-xs font-mono text-muted-foreground',
          SIZE_CLASSES[size],
          className
        )}
        title={`Flag not found: ${countryCode}`}
      >
        {fallback}
      </div>
    ) : (
      <div 
        className={cn(
          'flex items-center justify-center border border-border rounded-sm bg-muted text-xs font-mono text-muted-foreground',
          SIZE_CLASSES[size],
          className
        )}
        title={`Flag not found: ${countryCode}`}
      >
        {normalizedCode.toUpperCase()}
      </div>
    );
  }
  
  return (
    <div 
      className={cn(
        'flex items-center justify-center rounded-sm bg-cover bg-center bg-no-repeat overflow-hidden',
        SIZE_CLASSES[size],
        className
      )}
      title={`Flag of ${countryCode}`}
    >
      <img
        src={flagUrl}
        alt={`Flag of ${countryCode}`}
        className="w-full h-full object-cover"
        onError={handleError}
        onLoad={handleLoad}
        style={{ display: isLoading ? 'none' : 'block' }}
      />
      {/* Fallback image if primary fails and not already fallback */}
      {hasError && normalizedCode !== 'no' && (
        <img
          src="/icons/flag_svg/no.svg"
          alt="Fallback flag"
          className="w-full h-full object-cover"
          onError={() => setHasError(true)}
          onLoad={() => setHasError(false)}
        />
      )}
      {/* Loading state */}
      {isLoading && (
        <div className={cn(
          'flex items-center justify-center bg-muted text-xs text-muted-foreground',
          SIZE_CLASSES[size]
        )}>
          Loading...
        </div>
      )}
    </div>
  );
};